import React, { useMemo } from 'react';
import {
  Box, Button, IconButton, InputAdornment, List, ListItemButton, ListItemText, Paper, Stack, TextField,
  ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import * as api from '../plannerApi';
import * as T from '../types';
import { EmptyState, Loader, NeedsHousehold, PageHeader, useLoad } from '../common';
import { quantity, searchKey, sectionText, unitShort } from '../labels';

/** "Casa · Mercearia · un · despensa · 2 receitas". */
export function catalogSubtitle(item: T.CatalogItem): string {
  const parts = [sectionText(item.storeSection), unitShort(item.defaultUnit)];
  if (item.kind === 'Household') parts.unshift('Casa');
  if (item.needsReview) parts.unshift('Por rever');
  if (item.isPantryStaple) parts.push('despensa');
  if (item.minStock !== null && item.minStock !== undefined) parts.push(`mín. ${quantity(item.minStock, item.defaultUnit)}`);
  if (item.recipeCount > 0) parts.push(item.recipeCount === 1 ? '1 receita' : `${item.recipeCount} receitas`);
  if (item.preparedFromName) parts.push(`feito de ${item.preparedFromName}`);
  if (item.preparedItems.length > 0) parts.push(`→ ${item.preparedItems.map(p => p.name).join(', ')}`);
  return parts.join(' · ');
}

/** "review": made by the assistant, waiting for someone to check them. */
type KindFilter = 'all' | 'review' | T.CatalogItemKind;

/** The household's catalog of items (ingredients and household products). */
const CatalogList: React.FC = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const search = params.get('q') ?? '';
  const kind = (params.get('tipo') as KindFilter | null) ?? 'all';
  const { data, error, loading, reload } = useLoad(() => api.getCatalogItems());

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };

  const shown = useMemo(() => {
    const key = searchKey(search);
    return (data ?? [])
      .filter(i => kind === 'all' || (kind === 'review' ? i.needsReview : i.kind === kind))
      .filter(i => key.length === 0 || searchKey(i.name).includes(key));
  }, [data, search, kind]);

  return (
    <Box>
      <PageHeader
        eyebrow="Organizar"
        title="Artigos"
        subtitle="Artigos usados nas receitas e na lista de compras. A secção agrupa a lista por corredor."
        actions={(
          <>
            <Tooltip title="Atualizar"><IconButton onClick={reload}><RefreshIcon /></IconButton></Tooltip>
            <Button variant="contained" startIcon={<AddIcon />} component={RouterLink} to="novo">Novo artigo</Button>
          </>
        )}
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Procurar artigo"
          value={search}
          onChange={e => setParam('q', e.target.value)}
          sx={{ flexGrow: 1 }}
          slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }}
        />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={kind}
          onChange={(_, value: KindFilter | null) => value && setParam('tipo', value === 'all' ? '' : value)}
        >
          <ToggleButton value="all">Todos</ToggleButton>
          <ToggleButton value="Food">Alimentares</ToggleButton>
          <ToggleButton value="Household">Casa</ToggleButton>
          {(data ?? []).some(i => i.needsReview) && <ToggleButton value="review">Por rever</ToggleButton>}
        </ToggleButtonGroup>
      </Stack>
      <Loader loading={loading} error={error} onRetry={reload} hasData={data !== undefined}>
        {shown.length === 0 ? (
          (data ?? []).length === 0
            ? <EmptyState title="Sem artigos" text="São criados ao escrever receitas, ou toca em «Novo artigo»." />
            : <EmptyState title="Nenhum artigo encontrado" />
        ) : (
          <>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>{shown.length === 1 ? '1 artigo' : `${shown.length} artigos`}</Typography>
            <Paper variant="outlined" sx={{ p: 0.75 }}>
              <List disablePadding sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' }, columnGap: 0.5 }}>
                {shown.map(item => (
                  <ListItemButton key={item.publicId} onClick={() => navigate(item.publicId)} sx={{ borderRadius: 2 }}>
                    <ListItemText primary={item.name} secondary={catalogSubtitle(item)} slotProps={{ primary: { sx: { fontWeight: 600 } } }} />
                  </ListItemButton>
                ))}
              </List>
            </Paper>
          </>
        )}
      </Loader>
    </Box>
  );
};

const CatalogPage: React.FC = () => <NeedsHousehold><CatalogList /></NeedsHousehold>;

export default CatalogPage;
